////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Home hero — free-floating, full-viewport (1.0.3.md §4)         //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  The deliberate exception to "ALL content sections live in glass tiles"
//  (§5) — this is free-floating text over <BackgroundLayer />'s full-viewport
//  home image (which owns the parallax/video-slot mechanics; see that
//  component's "Home hero parallax" MARK). `-mt-20`/`min-h-screen` cancel
//  PublicLayout's `pt-20` header-height reserve so this still reaches true
//  viewport top rather than starting 5rem down (see that file's MARK comment).
//
//  Copy is Mike's, verbatim (1.0.3.md §4) — not a copywriting pass, so it is
//  not touched here beyond the word-by-word split the reveal needs. Each
//  visible line also carries a plain-text `sr-only` twin: the decorative
//  per-word `<span>` markup the mask reveal needs would otherwise read
//  strangely (or word-by-word, with pauses) to assistive tech.
//
//  ROUND-2 (12/08/2026): the hero's own scrim — the first 100vh's "smoked
//  overlay" — keeps its shape/behaviour exactly as shipped (a bottom-heavy
//  gradient fading toward the top, scrolling away WITH the hero itself,
//  Mike explicitly deferring further judgement on it) but is no longer a
//  fixed black-on-white treatment: it is now tinted with `--color-paper`
//  (light theme's paper is white -> a light/paper smoke; dark theme's paper
//  is near-black -> a dark/ink smoke), and the hero text follows the theme
//  `ink` token rather than a forced white. This fixes the theme mismatch
//  Mike flagged (nav chrome already theme-aware; hero text and smoke were
//  not) — see gui/debug/landing_dark_light_position0.png. Contrast for both
//  the title (`ink`) and subtitle (`muted`, via `.text-body-tertiary`) is
//  numerically re-verified across BackgroundLayer's full imagery grey range
//  in both themes in test/contrast.hero-veil.test.ts; the gradient's `via`
//  stop (85%) is the one that has to hold, since the hero's text block sits
//  vertically centred — i.e. right at that stop.
//
////////////////////////////////////////////////////////////////////////////////

import type { MouseEvent } from 'react';
import { motion } from 'framer-motion';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { ambientPulse, fadeRise, maskRevealWord, reduceVariants, staggerGroup } from '@/lib/motion';

const HEADLINE = 'A NEW ELEVATION.';
const SUBTITLE = 'Modern high-density construction, elevated by end-to-end control and relentless detail.';

/** id of the first content section — the CTA's smooth-scroll target and its
 * href="#..." fallback. Home.tsx's first section carries this id. */
export const HERO_SCROLL_TARGET_ID = 'content-start';

export function HomeHero () {
  const reducedMotion = useReducedMotion();

  const groupVariants = reduceVariants(staggerGroup, reducedMotion);
  const wordVariants = reduceVariants(maskRevealWord, reducedMotion);
  const fadeVariants = reduceVariants(fadeRise, reducedMotion);

  const words = HEADLINE.split(' ');

  const scrollToContent = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(HERO_SCROLL_TARGET_ID);
    if (!target) return; // href="#content-start" fallback still works if Home ever omits the id.
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  // Ambient "breathing" pulse on the CTA's arrows. The loop cannot be expressed
  // as one of lib/motion.ts's entrance `Variants` (a repeating breathe needs a
  // symmetric easeInOut, and reduceVariants' opacity-only/<=150ms/no-repeat
  // contract does not model a loop) — but its constants still live in
  // lib/motion.ts per §7's "no component defines its own curve"; `ambientPulse`
  // resolves the reduced-motion branch there too.
  const { animate: arrowPulse, transition: arrowPulseTransition } = ambientPulse(reducedMotion);

  return (
    <section aria-label="Hero" className="relative -mt-20 flex min-h-screen items-center overflow-hidden">
      {/* Theme-aware smoke (round-2, see this file's header comment) — a
          `--color-paper` tinted gradient, same bottom-heavy-fading-to-top
          shape the always-dark version shipped with. `via-paper/85` is the
          stop that matters: the text block below sits vertically centred in
          this `min-h-screen` section, landing right on it. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-paper/70 via-paper/60 to-paper/30" />

      <motion.div
        className="relative z-10 mx-auto w-full max-w-6xl px-6"
        variants={groupVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-2xl">
          <motion.h1 className="h1-huge text-ink" variants={groupVariants}>
            <span className="sr-only">{HEADLINE}</span>
            <span aria-hidden="true">
              {words.map((word, index) => (
                <span key={`${word}-${index}`} className="mr-3 inline-block overflow-hidden pb-1 align-bottom last:mr-0">
                  <motion.span className="inline-block" variants={wordVariants}>
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
          </motion.h1>

          <motion.p className="hero-subtitle mt-6 max-w-md" variants={fadeVariants}>
            {SUBTITLE}
          </motion.p>

          <motion.div variants={fadeVariants} className="mt-10">
            <a
              href={`#${HERO_SCROLL_TARGET_ID}`}
              onClick={scrollToContent}
              className="inline-flex items-center gap-3 text-sm font-medium uppercase tracking-widest text-ink outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ink/60"
            >
              <motion.span aria-hidden="true" animate={arrowPulse} transition={arrowPulseTransition}>
                &#8595;
              </motion.span>
              See the IRONSIDE standard
              <motion.span aria-hidden="true" animate={arrowPulse} transition={arrowPulseTransition}>
                &#8595;
              </motion.span>
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
